import * as Roles from '../../../../constants/role';
import { buildNsirDocumentoKmzFileName } from '../../../../utils/nsirDocumentoKmzName';
import { isServiceRow } from '../utils/nsmpRowTypes';

export const TIPO_CRU = 'CRU';
export const TIPO_INSPECAO = 'INSPECAO';
export const TIPO_COMPLETO = 'COMPLETO';

const ORDEM_KMZ = [TIPO_CRU, TIPO_INSPECAO, TIPO_COMPLETO];

const LABEL_KMZ = {
  CRU: 'KMZ matriz',
  INSPECAO: 'KMZ para inspeção',
  COMPLETO: 'KMZ completo',
};

const presente = (value) => value != null && String(value).trim() !== '';

const podeBaixarTodos = (role) =>
  role === Roles.SUPER_ADMIN || role === Roles.ADMIN || role === Roles.CROQUI;

export function rotuloCurtoKmz({ codigo, alimentadorCodigo }) {
  const codigoSeg = presente(codigo) ? String(codigo).trim() : '';
  const alimentadorSeg = presente(alimentadorCodigo) ? String(alimentadorCodigo).trim() : '';
  if (codigoSeg && alimentadorSeg) return `NSIR ${codigoSeg} · ${alimentadorSeg}`;
  if (codigoSeg) return `NSIR ${codigoSeg}`;
  return 'KMZ da NSIR';
}

export function sufixoArquivoKmz(tipo) {
  const chave = String(tipo || '').trim().toUpperCase();
  if (chave === TIPO_CRU) return 'matriz';
  if (chave === TIPO_INSPECAO) return 'inspecao';
  if (chave === TIPO_COMPLETO) return 'completo';
  return '';
}

export function nomeDownloadKmz(params, file, tipo) {
  const original = file && file.name ? file.name : 'arquivo.kmz';
  const nome = buildNsirDocumentoKmzFileName(params, original);
  const sufixo = sufixoArquivoKmz(tipo);
  if (!sufixo) return nome;
  const ponto = nome.lastIndexOf('.');
  if (ponto <= 0) return `${nome}_${sufixo}.kmz`;
  return `${nome.slice(0, ponto)}_${sufixo}${nome.slice(ponto)}`;
}

export function legendaKmz({ inspecao, completo }) {
  if (inspecao && completo) return 'Inspeção e completo';
  if (completo) return 'Completo';
  const extensao = extensaoDoArquivo(inspecao);
  if (extensao && extensao !== '.kmz' && extensao !== '.kml') return extensao.slice(1).toUpperCase();
  return 'Inspeção';
}

function item(tipo, file, label) {
  return { tipo, file, url: file && file.url, label };
}

export function resumoKmzCompleto(data, nsmps) {
  const qtd = Number(data && data.qtdServicosIncluidos);
  const incluidos = Number.isFinite(qtd) ? qtd : 0;
  const lista = Array.isArray(data && data.servicosSemCoordenada) ? data.servicosSemCoordenada : [];
  const nomes = lista
    .map((entry) => (entry && presente(entry.nome) ? String(entry.nome).trim() : ''))
    .filter(Boolean);
  const servicosNaNsir = (Array.isArray(nsmps) ? nsmps : []).filter(isServiceRow).length;
  const total = Math.max(servicosNaNsir, incluidos + lista.length, incluidos);
  return {
    incluidos,
    total,
    semCoordenada: lista.length,
    nomes,
  };
}

export function arquivosParaBaixar({ role, inspecao, completo }) {
  if (podeBaixarTodos(role)) {
    const itens = [];
    if (inspecao) itens.push(item(TIPO_INSPECAO, inspecao, 'KMZ para inspeção'));
    if (completo) itens.push(item(TIPO_COMPLETO, completo, 'KMZ completo'));
    return itens;
  }
  if (role === Roles.INSPETOR) {
    return inspecao ? [item(TIPO_INSPECAO, inspecao, 'KMZ para inspeção')] : [];
  }
  if (completo) return [item(TIPO_COMPLETO, completo, 'KMZ completo')];
  if (inspecao) return [item(TIPO_INSPECAO, inspecao, 'KMZ para inspeção')];
  return [];
}

const ID_SESSAO_LEGADO = 'legado';

function idSessaoReal(data) {
  if (!data || data.nsirKmzId == null) return null;
  const id = String(data.nsirKmzId).trim();
  if (!id || id.toLowerCase() === ID_SESSAO_LEGADO) return null;
  return id;
}

function sessaoConfirmada(data) {
  return Boolean(data && data.confirmadoEm != null && String(data.confirmadoEm).trim() !== '');
}

function tipoArquivoKmz(entry) {
  return String((entry && entry.tipo) || '').trim().toUpperCase();
}

export function rascunhoMatrizKmz(consultas) {
  const lista = (Array.isArray(consultas) ? consultas : [consultas]).filter(Boolean);
  if (!lista.length || lista.some(sessaoConfirmada)) return null;
  const arquivos = [];
  lista.forEach((data) => {
    const itens = Array.isArray(data.arquivos) ? data.arquivos : [];
    itens.forEach((entry) => {
      if (entry && entry.url) arquivos.push(entry);
    });
  });
  if (arquivos.some((entry) => tipoArquivoKmz(entry) === TIPO_INSPECAO)) return null;
  if (!arquivos.some((entry) => tipoArquivoKmz(entry) === TIPO_CRU)) return null;
  const origem = lista.find((data) => idSessaoReal(data));
  const nsirKmzId = idSessaoReal(origem);
  if (!nsirKmzId) return null;
  return {
    nsirKmzId,
    criadoEm: origem.criadoEm || null,
  };
}

export function arquivoVisivelKmz(arquivo, itens, nome) {
  if (arquivo != null) return arquivo;
  const lista = Array.isArray(itens) ? itens : [];
  const inspecao = lista.find((entry) => entry && entry.tipo === TIPO_INSPECAO && entry.url);
  if (!inspecao) return null;
  return { name: nome || 'arquivo.kmz', url: inspecao.url };
}

export function normalizarListaKmz(data) {
  const corpo = data && !Array.isArray(data) ? data : { arquivos: data };
  const lista = Array.isArray(corpo && corpo.arquivos) ? corpo.arquivos : [];
  const porTipo = new Map();
  lista.forEach((entry) => {
    if (!entry || !entry.url) return;
    const bruto = String(entry.tipo || '')
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    if (!LABEL_KMZ[bruto] || porTipo.has(bruto)) return;
    porTipo.set(bruto, { tipo: bruto, url: entry.url, label: LABEL_KMZ[bruto] });
  });
  return ORDEM_KMZ.map((tipo) => porTipo.get(tipo)).filter(Boolean);
}

export function itensParaVisualizar(role, itens) {
  const lista = Array.isArray(itens) ? itens : [];
  if (podeBaixarTodos(role)) return lista;
  if (role === Roles.INSPETOR) return lista.filter((entry) => entry.tipo === TIPO_INSPECAO);
  if (lista.some((entry) => entry.tipo === TIPO_COMPLETO)) {
    return lista.filter((entry) => entry.tipo === TIPO_COMPLETO);
  }
  return lista.filter((entry) => entry.tipo === TIPO_INSPECAO);
}

export function itemPadraoVisualizar(itens) {
  const lista = Array.isArray(itens) ? itens : [];
  return lista.find((entry) => entry.tipo === TIPO_COMPLETO)
    || lista.find((entry) => entry.tipo === TIPO_INSPECAO)
    || lista[0]
    || null;
}

export function extensaoDoArquivo(file) {
  const nome = file && file.name ? String(file.name) : '';
  const ponto = nome.lastIndexOf('.');
  if (ponto < 0) return '';
  return nome.slice(ponto).toLowerCase();
}

export function podeVisualizarArquivo(file) {
  if (!file) return false;
  const extensao = extensaoDoArquivo(file);
  if (!extensao) return true;
  return extensao === '.kmz' || extensao === '.kml' || extensao === '.pdf';
}
